import CloseIcon from "@mui/icons-material/CloseRounded";
import MergeIcon from "@mui/icons-material/MergeRounded";
import WarningIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";

import { ProceedingParticipantSearchResult } from "@/api";
import { useTranslation } from "@/translations";

import { MergeDestinationOption } from "./components/MergeDestinationOption";
import { useMergeParticipantsDialog } from "./hooks/useMergeParticipantsDialog";

interface Props {
  selectedParticipants: ProceedingParticipantSearchResult[];
  onClose: () => void;
  onMerged: () => void;
}

export function MergeParticipantsDialog({ selectedParticipants, onClose, onMerged }: Props) {
  const { t } = useTranslation();
  const { destination, source, setDestinationId, confirmMerge, isPending } = useMergeParticipantsDialog(
    selectedParticipants,
    onMerged,
    onClose
  );

  if (!source || !destination) {
    return null;
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ p: 5, pb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}
        variant="h1"
      >
        {t("proceedingParticipants.merge.dialogTitle")}
        <IconButton onClick={onClose} aria-label={t("common.close")}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 5, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          <Typography variant="body2">{t("proceedingParticipants.merge.dialogIntro")}</Typography>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            <RadioGroup
              name="merge-destination"
              value={destination.proceedingParticipantId}
              onChange={(e) => setDestinationId(e.target.value)}
            >
              <Stack spacing={1}>
                {selectedParticipants.map((participant) => (
                  <MergeDestinationOption
                    key={participant.proceedingParticipantId}
                    participant={participant}
                    isDestination={participant.proceedingParticipantId === destination.proceedingParticipantId}
                  />
                ))}
              </Stack>
            </RadioGroup>
          </Box>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <WarningIcon color="error" fontSize="small" />
            <Typography variant="body2" color="error">
              {t("proceedingParticipants.merge.sourcesWarning", {
                source: source.name,
                destination: destination.name,
              })}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{ p: 5, pt: 0, gap: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Button variant="outlined" onClick={onClose} disabled={isPending}>
          {t("proceedingParticipants.merge.cancelButton")}
        </Button>
        <Button variant="contained" startIcon={<MergeIcon />} onClick={confirmMerge} disabled={isPending}>
          {t("proceedingParticipants.merge.confirmButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
